import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../helpers/Axios";

import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Checkbox,
  Paper
} from "@mui/material";

import { styled } from "@mui/system";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import baseURL from "../../config/config";
import AdminHeader from "../AdminHeader/AdminHeader";
import LoadingScreen from "../../Components/Student/LoadingScreen";

// ---------------- Styles ----------------
const PageWrapper = styled(Box)`
  padding: 1.5rem;
  background-color: #f1f5f9;
  min-height: 100vh;
`;

const TableRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  padding: 6px 8px;
  border-bottom: 1px solid #e2e8f0;
  background: white;
  font-size: 14px;
`;

const TableHeader = styled(TableRow)`
  background: #e8eef5;
  font-weight: 700;
`;

// Add Row component - it's the same as TableRow
const Row = styled(Box)`
  display: flex;
  align-items: flex-start;
  padding: 6px 8px;
  border-bottom: 1px solid #e2e8f0;
  background: white;
  font-size: 14px;
`;

const HeaderCell = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const FloatingActions = styled(Paper)`
  position: fixed;
  bottom: 25px;
  right: 25px;
  padding: 1rem;
  border-radius: 12px;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

export default function WordFilterPage() {
  const ENDPOINT = `${baseURL}/api/trail/word-audios/`;
  const EXPERTS_ENDPOINT = `${baseURL}/api/core/experts/`;

  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [notify, setNotify] = useState(null);
  const [selectedWords, setSelectedWords] = useState(new Set());
  const [error, setError] = useState(null);

  const [currentAudio, setCurrentAudio] = useState(null);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  const [pageSize, setPageSize] = useState(200);

  const [pagination, setPagination] = useState({
    next: null,
    previous: null
  });

  const [filters, setFilters] = useState({
    search: "",
    expertId: ""
  });

  const [executeSearch, setExecuteSearch] = useState(false);

  // Auto-hide toast
  useEffect(() => {
    if (notify) {
      const t = setTimeout(() => setNotify(null), 2500);
      return () => clearTimeout(t);
    }
  }, [notify]);

  // -------- Fetch Words --------
  const fetchWords = useCallback(
    async (url = ENDPOINT) => {
      try {
        setLoading(true);

        const params = { page_size: pageSize };

        if (filters.search) params.word = filters.search;
        if (filters.expertId) params.expert_id = filters.expertId;

        if (sortField)
          params.ordering = `${sortOrder === "desc" ? "-" : ""}${sortField}`;

        const res = await axiosInstance.get(url, { params });

        const data = res.data?.data || {};

        setWords(data.results);
        setPagination({
          next: data.next,
          previous: data.previous
        });
      } catch {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    },
    [filters, sortField, sortOrder, pageSize]
  );

  // Loudness Calculation
  const computeLoudness = async () => {
    try {
      setLoading(true);
      const payload = { word_ids: Array.from(selectedWords) };

      await axiosInstance.post(
        `${baseURL}/api/trail/compute-loudness/`,
        payload
      );

      await fetchWords();
      setNotify("Loudness computed successfully!");
    } catch {
      setNotify("Failed to compute loudness");
    } finally {
      setLoading(false);
    }
  };

  // Initial Load
  useEffect(() => {
    fetchWords();
  }, []);

  // Re-load when search/sort triggers
  useEffect(() => {
    if (executeSearch) {
      fetchWords();
      setExecuteSearch(false);
    }
  }, [executeSearch]);

  // -------- Helpers --------
  const toggleSelect = (id) => {
    setSelectedWords((prev) => {
      const ns = new Set(prev);
      ns.has(id) ? ns.delete(id) : ns.add(id);
      return ns;
    });
  };

  const handleSort = (field) => {
    setSortOrder(sortField === field && sortOrder === "asc" ? "desc" : "asc");
    setSortField(field);
    setExecuteSearch(true);
  };

  const bulkSelectTopN = (n) => {
    const ids = words.slice(0, n).map((w) => w.id);
    setSelectedWords(new Set(ids));
  };

  const handlePlayAudio = (url) =>
    setCurrentAudio((prev) => (prev === url ? null : url));

  // ---------------- Render ----------------
  return (
    <>
      <AdminHeader />

      {loading && <LoadingScreen />}

      {notify && (
        <Paper
          sx={{
            position: "fixed",
            top: 70,
            right: 20,
            padding: "12px 18px",
            borderRadius: "8px",
            backgroundColor: "#0f766e",
            color: "white",
            zIndex: 999
          }}
        >
          {notify}
        </Paper>
      )}

      <PageWrapper>
        <Typography
          marginTop="80px"
          variant="h5"
          fontWeight={600}
          textAlign="center"
          mb={2}
        >
          Word Listing
        </Typography>

        {/* SEARCH */}
        <Box display="flex" gap={1} mb={2} width="45vw">
          <TextField
            size="small"
            label="Search word"
            fullWidth
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && setExecuteSearch(true)}
          />

          <Button variant="contained" onClick={() => setExecuteSearch(true)}>
            Search
          </Button>
        </Box>

        {/* PAGE SIZE BUTTON */}
        <Box display="flex" gap={2} mb={2}>
          <Button
            variant="outlined"
            sx={{ color: "green" }}
            onClick={() => {
              setPageSize(pageSize === 1000 ? 100 : 1000);
              setExecuteSearch(true);
            }}
          >
            {pageSize === 1000 ? "Show 100 per page" : "Show 1000 per page"}
          </Button>

          <Button variant="outlined" onClick={() => bulkSelectTopN(100)}>
            Select First 100
          </Button>

          <Button variant="outlined" onClick={() => bulkSelectTopN(1000)}>
            Select First 1000
          </Button>

          <Button
            variant="outlined"
            color="error"
            onClick={() => setSelectedWords(new Set())}
          >
            Clear Selection
          </Button>
        </Box>

        {/* HEADER */}
        <Row sx={{ fontWeight: 600, background: "#eef2f6" }}>
          <Box width="80px">Select</Box>
          <Box width="80px" sx={{ cursor: "pointer", color: "burlywood" }} onClick={() => handleSort("id")}>
            Word ID
          </Box>
          <Box flex={1} sx={{ cursor: "pointer", color: "burlywood" }} onClick={() => handleSort("word")}>
            Word
          </Box>
          <Box flex={1}>Part of Speech</Box>
          <Box width="600px">Expert | Audio | Loudness | Label</Box>
        </Row>

        {/* ROWS */}
        {words.map((item) => (
          <Row key={item.id}>
            <Box width="80px">
              <Checkbox
                checked={selectedWords.has(item.id)}
                onChange={() => toggleSelect(item.id)}
              />
            </Box>

            <Box width="80px">{item.id}</Box>
            <Box flex={1}>{item.word}</Box>
            <Box flex={1}>{item.part_of_speech}</Box>

            {/* PERFECTLY ALIGNED EXPERT ROWS */}
            <Box width="600px" sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {item.expert_audios?.map((a) => (
                <Box
                  key={a.expert_audio}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    borderBottom: "1px dashed #e5e7eb",
                    paddingBottom: "3px"
                  }}
                >
                  {/* Expert name */}
                  <Box sx={{ width: "150px" }}>
                    <Typography variant="caption">
                      {a.expert_name} {a.default ? "(default)" : ""}
                    </Typography>
                  </Box>

                  {/* Audio */}
                  <Box sx={{ width: "150px" }}>
                    <IconButton size="small" onClick={() => handlePlayAudio(a.expert_audio)}>
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                    {currentAudio === a.expert_audio && (
                      <audio controls autoPlay style={{ width: "100%" }}>
                        <source src={a.expert_audio} />
                      </audio>
                    )}
                  </Box>

                  {/* Loudness */}
                  <Box sx={{ width: "150px" }}>
                    <Typography variant="caption">{a.loudness_display ?? "-"}</Typography>
                  </Box>

                  {/* Label */}
                  <Box sx={{ width: "150px" }}>
                    <Typography variant="caption">{a.loudness_label ?? "-"}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Row>
        ))}

        {/* PAGINATION */}
        <Box display="flex" justifyContent="center" gap={2} mt={3}>
          <Button
            variant="outlined"
            disabled={!pagination.previous}
            onClick={() => fetchWords(pagination.previous)}
          >
            Previous
          </Button>

          <Button
            variant="outlined"
            disabled={!pagination.next}
            onClick={() => fetchWords(pagination.next)}
          >
            Next
          </Button>
        </Box>

        {/* BULK LOUDNESS */}
        {selectedWords.size > 0 && (
          <FloatingActions>
            <Button variant="contained" onClick={computeLoudness}>
              Compute Loudness ({selectedWords.size})
            </Button>
          </FloatingActions>
        )}
      </PageWrapper>
    </>
  );
}